import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, doc, updateDoc, query, orderBy, where, addDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential, updateProfile, sendEmailVerification } from 'firebase/auth';
import { xpService } from '../services/xpService';
import { UserXP, LEVEL_CONFIGS, calculateLevel } from '../types/xp';
import VenueRequestForm from './VenueRequestForm';

interface Player {
  uid: string;
  displayName: string;
  email: string;
  username?: string;
}

interface Friendship {
  id: string;
  userId1: string;
  userId2: string;
  username1: string;
  username2: string;
  createdAt: Date;
  status: string;
}

interface FriendRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  fromUsername: string;
  toUsername: string;
  status: string;
  createdAt: Date;
  respondedAt?: Date;
}

interface PlayerStats {
  totalGames: number;
  wins: number;
  losses: number;
  winRate: number;
  averageScore: number;
  highestCheckout: number;
  total180s: number;
  totalCheckouts: number;
  checkoutRate: number;
  favoriteGame: string;
  totalPlayTime: number;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: string;
  requirementType?: string;
  requirementValue?: number;
  category?: string;
  rarity?: string;
}

interface MatchHistory {
  id: string;
  opponent: string;
  result: 'win' | 'loss' | 'draw';
  score: string;
  gameMode: string;
  date: string;
  venue: string;
}

const PlayerProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'stats' | 'history' | 'achievements' | 'friends' | 'requests' | 'settings' | 'operation'>('stats');
  const [viewingPlayer, setViewingPlayer] = useState<Player | null>(null);
  const [showPlayerSearch, setShowPlayerSearch] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userXP, setUserXP] = useState<UserXP | null>(null);
  const [xpHistory, setXPHistory] = useState<any[]>([]);
  const [isXPHistoryExpanded, setIsXPHistoryExpanded] = useState(false);
  const [isLoadingXP, setIsLoadingXP] = useState(true);

  // Friends state
  const [friends, setFriends] = useState<Friendship[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<FriendRequest[]>([]);
  const [showAddFriendDialog, setShowAddFriendDialog] = useState(false);
  const [friendSearchTerm, setFriendSearchTerm] = useState('');
  const [searchedUser, setSearchedUser] = useState<Player | null>(null);
  const [friendMessage, setFriendMessage] = useState('');
  const [isLoadingFriends, setIsLoadingFriends] = useState(false);
  const [friendsSubTab, setFriendsSubTab] = useState<'friends' | 'incoming' | 'outgoing'>('friends');

  // Check if user signed in with Google
  const isGoogleUser = user?.providerData.some(provider => provider.providerId === 'google.com') || false;

  // Check URL query parameter for tab
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'operation') {
      setActiveTab('operation');
    }
  }, []);

  // Settings form state
  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [passwordForAuth, setPasswordForAuth] = useState(''); // For re-authentication when changing email
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<Set<string>>(new Set());
  const [isLoadingAchievements, setIsLoadingAchievements] = useState(true);
  const [achievementFilter, setAchievementFilter] = useState<'all' | 'unlocked' | 'locked'>('all');
  const [achievementSort, setAchievementSort] = useState<'unlocked-first' | 'locked-first' | 'name' | 'rarity'>('unlocked-first');
  const [achievementCategory, setAchievementCategory] = useState<'all' | 'wins' | 'matches' | 'friends' | 'skill' | 'other'>('all');

  const [matchHistory, setMatchHistory] = useState<MatchHistory[]>([]);

  // Calculate stats from match history
  const calculateStats = (): PlayerStats => {
    const wins = matchHistory.filter(m => m.result === 'win').length;
    const losses = matchHistory.filter(m => m.result === 'loss').length;
    const totalGames = matchHistory.length;
    const winRate = totalGames > 0 ? (wins / totalGames) * 100 : 0;

    return {
      totalGames,
      wins,
      losses,
      winRate: Math.round(winRate * 10) / 10,
      averageScore: 45.8,
      highestCheckout: 170,
      total180s: 23,
      totalCheckouts: 134,
      checkoutRate: 38.5,
      favoriteGame: '501',
      totalPlayTime: 2840 // minutes
    };
  };

  const stats = calculateStats();

  // Calculate user progress for achievement requirements
  const calculateAchievementProgress = (requirementType: string): number => {
    if (!requirementType) return 0;

    const type = requirementType.toLowerCase();

    // Map requirement types to stats
    if (type.includes('win') || type.includes('győzelem')) {
      return stats.wins;
    } else if (type.includes('match') || type.includes('mérkőzés') || type.includes('játék')) {
      return stats.totalGames;
    } else if (type.includes('180')) {
      return stats.total180s;
    } else if (type.includes('checkout')) {
      return stats.totalCheckouts;
    }

    return 0;
  };

  // Calculate total XP from match history
  const calculateMatchHistoryXP = () => {
    return matchHistory.reduce((total, match) => {
      if (match.result === 'win') return total + 30;
      if (match.result === 'draw') return total + 20;
      return total + 10; // loss
    }, 0);
  };

  const matchHistoryXP = calculateMatchHistoryXP();

  useEffect(() => {
    loadPlayers();
    loadMatchHistory();
    loadFriends();
    loadFriendRequests();
    loadOutgoingRequests();
    loadUserUsername();
    loadAchievements();
  }, [user]);

  // Load username from Firestore
  const loadUserUsername = async () => {
    if (!user) return;
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUsername(userData.username || '');
      }
    } catch (error) {
      console.error('Error loading username:', error);
    }
  };

  // Recalculate XP whenever matchHistory changes
  useEffect(() => {
    if (matchHistory.length > 0) {
      loadUserXP();
    }
  }, [matchHistory]);

  const loadMatchHistory = async () => {
    if (!user) return;
    try {
      const matchesQuery = query(
        collection(db, 'match_history'),
        orderBy('date', 'desc')
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      const matches: MatchHistory[] = [];

      matchesSnapshot.forEach((doc) => {
        const data = doc.data() as any;
        // Only load matches for the current user
        if (data.userId === user.uid) {
          matches.push({
            id: doc.id,
            opponent: data.opponent || 'Unknown',
            result: data.result || 'loss',
            score: data.score || '0-0',
            gameMode: data.gameMode || '501',
            date: data.date?.toDate?.()?.toISOString().split('T')[0] || data.date || new Date().toISOString().split('T')[0],
            venue: data.venue || 'Unknown'
          });
        }
      });

      setMatchHistory(matches);
    } catch (error) {
      console.error('Error loading match history:', error);
    }
  };

  const loadUserXP = async () => {
    if (!user) {
      setIsLoadingXP(false);
      return;
    }
    try {
      setIsLoadingXP(true);
      // Calculate XP based on match history
      const calculatedXP = calculateMatchHistoryXP();
      const levelData = calculateLevel(calculatedXP);

      // Create UserXP object from calculated data
      const calculatedUserXP: UserXP = {
        userId: user.uid,
        totalXP: calculatedXP,
        level: levelData.level,
        currentLevelXP: levelData.currentLevelXP,
        nextLevelXP: levelData.nextLevelXP,
        lastUpdated: new Date()
      };

      setUserXP(calculatedUserXP);
      setIsLoadingXP(false);

      // Generate XP history from match history
      const generatedHistory = matchHistory.map((match, index) => {
        let amount = 10; // loss
        let description = 'Vereség';
        if (match.result === 'win') {
          amount = 30;
          description = 'Győzelem';
        } else if (match.result === 'draw') {
          amount = 20;
          description = 'Döntetlen';
        }

        return {
          id: match.id,
          userId: user.uid,
          amount,
          source: 'match' as const,
          sourceId: match.id,
          description: `${description} - ${match.opponent}`,
          timestamp: new Date(match.date),
          metadata: {
            matchResult: match.result
          }
        };
      });

      setXPHistory(generatedHistory);
    } catch (error) {
      console.error('Error loading user XP:', error);
      setIsLoadingXP(false);
    }
  };

  const loadPlayers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const playersData: Player[] = [];
      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        const displayName = data.displayName || 'Játékos';

        // Skip users with generic/default names or missing email
        if (displayName === 'Játékos' || displayName === 'User' || !data.email) {
          return;
        }

        playersData.push({
          uid: doc.id,
          displayName: displayName,
          email: data.email
        });
      });
      setPlayers(playersData);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  const filteredPlayers = players.filter(player =>
    player.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    player.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentPlayer = viewingPlayer || {
    uid: user?.uid || '',
    displayName: user?.displayName || 'Játékos',
    email: user?.email || ''
  };

  const formatPlayTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}ó ${mins}p`;
  };

  // Load achievements from Firebase
  const loadAchievements = async () => {
    if (!user) {
      setIsLoadingAchievements(false);
      return;
    }

    try {
      setIsLoadingAchievements(true);

      // Load all achievements from the achievements collection
      const achievementsSnapshot = await getDocs(collection(db, 'achievements'));
      const achievementsData: Achievement[] = [];

      achievementsSnapshot.forEach((doc) => {
        const data = doc.data();
        achievementsData.push({
          id: doc.id,
          title: data.name || data.title || 'Achievement',
          description: data.description || '',
          icon: data.icon || '🏆',
          unlocked: false,
          unlockedAt: undefined,
          requirementType: data.requirementType || '',
          requirementValue: data.requirementValue || 0,
          category: data.category || 'general',
          rarity: data.rarity || 'common'
        });
      });

      // Load user's unlocked achievements
      const userAchievementsQuery = query(
        collection(db, 'user_achievements'),
        where('userId', '==', user.uid)
      );
      const userAchievementsSnapshot = await getDocs(userAchievementsQuery);
      const unlockedIds = new Set<string>();
      const unlockedDates: { [key: string]: string } = {};

      userAchievementsSnapshot.forEach((doc) => {
        const data = doc.data();
        unlockedIds.add(data.achievementId);
        unlockedDates[data.achievementId] = data.unlockedAt?.toDate?.()?.toISOString() || new Date().toISOString();
      });

      // Mark achievements as unlocked if user has them
      const finalAchievements = achievementsData.map(achievement => ({
        ...achievement,
        unlocked: unlockedIds.has(achievement.id),
        unlockedAt: unlockedDates[achievement.id]
      }));

      setAchievements(finalAchievements);
      setUserAchievements(unlockedIds);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setIsLoadingAchievements(false);
    }
  };

  // Load friends
  const loadFriends = async () => {
    if (!user) return;
    setIsLoadingFriends(true);
    try {
      // Query where userId1 == currentUser
      const query1 = query(
        collection(db, 'friendships'),
        where('userId1', '==', user.uid),
        where('status', '==', 'active')
      );

      // Query where userId2 == currentUser
      const query2 = query(
        collection(db, 'friendships'),
        where('userId2', '==', user.uid),
        where('status', '==', 'active')
      );

      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(query1),
        getDocs(query2)
      ]);

      const friendshipsData: Friendship[] = [];

      snapshot1.forEach((doc) => {
        const data = doc.data();
        friendshipsData.push({
          id: doc.id,
          userId1: data.userId1,
          userId2: data.userId2,
          username1: data.username1,
          username2: data.username2,
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status
        });
      });

      snapshot2.forEach((doc) => {
        const data = doc.data();
        friendshipsData.push({
          id: doc.id,
          userId1: data.userId1,
          userId2: data.userId2,
          username1: data.username1,
          username2: data.username2,
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status
        });
      });

      setFriends(friendshipsData);
    } catch (error) {
      console.error('Error loading friends:', error);
    } finally {
      setIsLoadingFriends(false);
    }
  };

  // Load friend requests
  const loadFriendRequests = async () => {
    if (!user) return;
    try {
      // Get incoming requests - without orderBy to avoid index requirement
      const incomingQuery = query(
        collection(db, 'friend_requests'),
        where('toUserId', '==', user.uid),
        where('status', '==', 'pending')
      );

      const snapshot = await getDocs(incomingQuery);
      const requestsData: FriendRequest[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        requestsData.push({
          id: doc.id,
          fromUserId: data.fromUserId,
          toUserId: data.toUserId,
          fromUsername: data.fromUsername,
          toUsername: data.toUsername,
          status: data.status,
          createdAt: data.createdAt?.toDate() || new Date(),
          respondedAt: data.respondedAt?.toDate()
        });
      });

      // Sort manually by createdAt
      requestsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      setFriendRequests(requestsData);
    } catch (error) {
      console.error('Error loading friend requests:', error);
    }
  };

  // Load outgoing friend requests
  const loadOutgoingRequests = async () => {
    if (!user) return;
    try {
      // Get outgoing requests - without orderBy to avoid index requirement
      const outgoingQuery = query(
        collection(db, 'friend_requests'),
        where('fromUserId', '==', user.uid),
        where('status', '==', 'pending')
      );

      const snapshot = await getDocs(outgoingQuery);
      const requestsData: FriendRequest[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        requestsData.push({
          id: doc.id,
          fromUserId: data.fromUserId,
          toUserId: data.toUserId,
          fromUsername: data.fromUsername,
          toUsername: data.toUsername,
          status: data.status,
          createdAt: data.createdAt?.toDate() || new Date(),
          respondedAt: data.respondedAt?.toDate()
        });
      });

      // Sort manually by createdAt
      requestsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      setOutgoingRequests(requestsData);
    } catch (error) {
      console.error('Error loading outgoing requests:', error);
    }
  };

  // Search user by username
  const searchUserByUsername = async () => {
    if (!friendSearchTerm.trim()) {
      setFriendMessage('Kérlek adj meg egy felhasználónevet');
      return;
    }

    setIsLoadingFriends(true);
    setSearchedUser(null);
    setFriendMessage('');

    try {
      // Search by username (case-insensitive, trim whitespace)
      const searchUsername = friendSearchTerm.trim();
      const usersQuery = query(
        collection(db, 'users')
      );

      const snapshot = await getDocs(usersQuery);

      // Filter manually for case-insensitive match
      const matchingDocs = snapshot.docs.filter(doc => {
        const username = doc.data().username || '';
        return username.toLowerCase() === searchUsername.toLowerCase();
      });

      if (matchingDocs.length === 0) {
        setFriendMessage('Felhasználó nem található');
        setIsLoadingFriends(false);
        return;
      }

      const userDoc = matchingDocs[0];
      const userData = userDoc.data();

      if (userDoc.id === user?.uid) {
        setFriendMessage('Nem adhatod hozzá magad barátként');
        setIsLoadingFriends(false);
        return;
      }

      // Check if already friends
      const areFriends = friends.some(f =>
        (f.userId1 === user?.uid && f.userId2 === userDoc.id) ||
        (f.userId2 === user?.uid && f.userId1 === userDoc.id)
      );

      if (areFriends) {
        setFriendMessage('Már barátok vagytok');
        setIsLoadingFriends(false);
        return;
      }

      // Check if there's already an outgoing request
      const hasOutgoingRequest = outgoingRequests.some(r =>
        r.toUserId === userDoc.id
      );

      if (hasOutgoingRequest) {
        setFriendMessage('Már küldtél barátkérést ennek a felhasználónak');
        setIsLoadingFriends(false);
        return;
      }

      // Check if there's already an incoming request
      const hasIncomingRequest = friendRequests.some(r =>
        r.fromUserId === userDoc.id
      );

      if (hasIncomingRequest) {
        setFriendMessage('Ez a felhasználó már küldött neked barátkérést! Nézd meg a Bejövő kérelmek tab-ot.');
        setIsLoadingFriends(false);
        return;
      }

      setSearchedUser({
        uid: userDoc.id,
        username: userData.username,
        displayName: userData.displayName || userData.username,
        email: userData.email
      });
      setFriendMessage('');
    } catch (error) {
      console.error('Error searching user:', error);
      setFriendMessage('Hiba történt a keresés során');
    } finally {
      setIsLoadingFriends(false);
    }
  };

  // Send friend request
  const sendFriendRequest = async () => {
    if (!user || !searchedUser) return;

    setIsLoadingFriends(true);
    try {
      // Get current user's username from users collection using document ID
      const currentUserDocRef = doc(db, 'users', user.uid);
      const currentUserDoc = await getDoc(currentUserDocRef);

      let currentUsername = 'user';
      if (currentUserDoc.exists()) {
        currentUsername = currentUserDoc.data().username || 'user';
      }

      await addDoc(collection(db, 'friend_requests'), {
        fromUserId: user.uid,
        toUserId: searchedUser.uid,
        fromUsername: currentUsername,
        toUsername: searchedUser.username,
        status: 'pending',
        createdAt: new Date()
      });

      setFriendMessage('Barátkérés elküldve!');
      setSearchedUser(null);
      setFriendSearchTerm('');
      setTimeout(() => setShowAddFriendDialog(false), 1500);

      // Reload outgoing requests to show the new request
      loadOutgoingRequests();
    } catch (error) {
      console.error('Error sending friend request:', error);
      setFriendMessage('Hiba történt a barátkérés küldése során');
    } finally {
      setIsLoadingFriends(false);
    }
  };

  // Accept friend request
  const acceptFriendRequest = async (request: FriendRequest) => {
    try {
      // Get actual usernames from users collection using document IDs
      const fromUserDocRef = doc(db, 'users', request.fromUserId);
      const fromUserDoc = await getDoc(fromUserDocRef);

      const toUserDocRef = doc(db, 'users', request.toUserId);
      const toUserDoc = await getDoc(toUserDocRef);

      const fromUsername = fromUserDoc.exists() ? fromUserDoc.data().username : request.fromUsername;
      const toUsername = toUserDoc.exists() ? toUserDoc.data().username : request.toUsername;

      // Update request status
      await updateDoc(doc(db, 'friend_requests', request.id), {
        status: 'accepted',
        respondedAt: new Date()
      });

      // Create friendship with verified usernames
      const sortedIds = [request.fromUserId, request.toUserId].sort();
      const sortedUsernames = request.fromUserId === sortedIds[0]
        ? [fromUsername, toUsername]
        : [toUsername, fromUsername];

      await addDoc(collection(db, 'friendships'), {
        userId1: sortedIds[0],
        userId2: sortedIds[1],
        username1: sortedUsernames[0],
        username2: sortedUsernames[1],
        createdAt: new Date(),
        status: 'active'
      });

      // Reload friends and requests
      loadFriends();
      loadFriendRequests();
    } catch (error) {
      console.error('Error accepting friend request:', error);
    }
  };

  // Reject friend request
  const rejectFriendRequest = async (requestId: string) => {
    try {
      await updateDoc(doc(db, 'friend_requests', requestId), {
        status: 'rejected',
        respondedAt: new Date()
      });

      loadFriendRequests();
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    }
  };

  // Remove friend
  const removeFriend = async (friendshipId: string) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a barátot?')) return;

    try {
      await deleteDoc(doc(db, 'friendships', friendshipId));
      loadFriends();
    } catch (error) {
      console.error('Error removing friend:', error);
    }
  };

  // Cancel outgoing friend request
  const cancelFriendRequest = async (requestId: string) => {
    if (!window.confirm('Biztosan visszavonod ezt a barátkérést?')) return;

    try {
      await updateDoc(doc(db, 'friend_requests', requestId), {
        status: 'cancelled',
        respondedAt: new Date()
      });

      loadOutgoingRequests();
    } catch (error) {
      console.error('Error cancelling friend request:', error);
    }
  };

  const handleProfileUpdate = async () => {
    if (!user) return;

    try {
      setErrorMessage('');
      setSaveMessage('');

      // Check if email is being changed without password
      if (!isGoogleUser && email !== user.email && !passwordForAuth) {
        setErrorMessage(t('playerProfile.passwordRequiredForEmail'));
        setTimeout(() => setErrorMessage(''), 5000);
        return;
      }

      let emailChanged = false;

      // Update display name
      if (displayName !== user.displayName) {
        await updateProfile(user, { displayName });
        // Update in Firestore
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, { displayName });
      }

      // Update username in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentUsername = userDoc.data().username;
        if (username && username !== currentUsername) {
          await updateDoc(userDocRef, { username: username.toLowerCase() });
        }
      }

      // Update email if changed and not a Google user
      if (!isGoogleUser && email !== user.email && passwordForAuth) {
        console.log('Starting email update process...');
        console.log('Old email:', user.email);
        console.log('New email:', email);

        // Re-authenticate user before changing email
        const credential = EmailAuthProvider.credential(user.email!, passwordForAuth);
        await reauthenticateWithCredential(user, credential);
        console.log('Re-authentication successful');

        // Update email in Firebase Auth
        await updateEmail(user, email);
        console.log('Firebase Auth email updated successfully');

        // Send email verification to new email address
        await sendEmailVerification(user);
        console.log('Verification email sent to new address');

        // Update in Firestore
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, { email });
        console.log('Firestore email updated successfully');

        // Force reload the user to get updated auth state
        await user.reload();
        console.log('User reloaded, new email:', auth.currentUser?.email);

        emailChanged = true;
      }

      setEditMode(false);
      setPasswordForAuth('');

      // Show appropriate success message
      if (emailChanged) {
        setSaveMessage(t('playerProfile.emailChangedWithVerification'));
      } else {
        setSaveMessage(t('playerProfile.profileUpdated'));
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      if (error.code === 'auth/wrong-password') {
        setErrorMessage(t('playerProfile.wrongPassword'));
      } else if (error.code === 'auth/requires-recent-login') {
        setErrorMessage(t('playerProfile.requiresRecentLogin'));
      } else if (error.code === 'auth/email-already-in-use') {
        setErrorMessage(t('playerProfile.emailInUse'));
      } else if (error.code === 'auth/invalid-credential') {
        setErrorMessage(t('playerProfile.wrongPassword'));
      } else if (error.code === 'auth/operation-not-allowed') {
        setErrorMessage(t('playerProfile.operationNotAllowed'));
      } else {
        setErrorMessage(`${t('playerProfile.updateError')} (${error.code || 'unknown'})`);
      }
      setTimeout(() => setErrorMessage(''), 8000);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!user || !currentPassword || !newPassword || !confirmPassword) {
      setErrorMessage(t('playerProfile.fillAllFields'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage(t('playerProfile.passwordMismatch'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage(t('playerProfile.passwordTooShort'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      setErrorMessage('');
      setSaveMessage('');

      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(user.email!, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      setSaveMessage(t('playerProfile.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        setErrorMessage(t('playerProfile.wrongPassword'));
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage(t('playerProfile.passwordTooShort'));
      } else {
        setErrorMessage(t('playerProfile.updateError'));
      }
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-3xl sm:text-4xl font-bold text-gradient-cyan whitespace-nowrap">
          🎯 {t('playerProfile.title')}
        </h2>
        {viewingPlayer && (
          <button
            onClick={() => setViewingPlayer(null)}
            className="btn-secondary flex items-center gap-2 justify-center whitespace-nowrap text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="hidden sm:inline">{t('playerProfile.backToMyProfile')}</span>
            <span className="sm:hidden">Vissza</span>
          </button>
        )}
      </div>

      {/* Player Search Modal */}
      {showPlayerSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="card-dark p-8 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gradient-cyan">{t('playerProfile.searchPlayers')}</h3>
              <button
                onClick={() => setShowPlayerSearch(false)}
                className="text-[#B8B8C3] hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <input
                type="text"
                placeholder={t('playerProfile.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-dark w-full"
              />
            </div>

            <div className="space-y-2">
              {filteredPlayers.length === 0 ? (
                <p className="text-center text-[#B8B8C3] py-8">{t('playerProfile.noPlayersFound')}</p>
              ) : (
                filteredPlayers.map((player) => (
                  <button
                    key={player.uid}
                    onClick={() => {
                      setViewingPlayer(player);
                      setShowPlayerSearch(false);
                      setSearchTerm('');
                    }}
                    className="w-full p-4 card-dark-hover text-left flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-[#00AFF2] to-[#0089be] rounded-full flex items-center justify-center">
                      <span className="text-xl font-bold text-white">
                        {player.displayName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold">{player.displayName}</p>
                      <p className="text-sm text-[#B8B8C3]">{player.email}</p>
                    </div>
                    {player.uid === user?.uid && (
                      <span className="text-xs text-[#00AFF2]">{t('playerProfile.you')}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Player Info Card */}
      <div className="card-dark p-6">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
          <div className="relative flex-shrink-0">
            <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-[#00AFF2] to-[#0089be] rounded-full flex items-center justify-center">
              <span className="text-4xl sm:text-5xl font-bold text-white">
                {(currentPlayer.username || currentPlayer.displayName).charAt(0).toUpperCase()}
              </span>
            </div>
            {userXP && (
              <div className={`absolute -bottom-2 -right-2 px-2 sm:px-3 py-1 rounded-full bg-gradient-to-r ${userXP.level >= 15 ? 'from-purple-500 to-pink-500' :
                userXP.level >= 10 ? 'from-yellow-500 to-orange-500' :
                  userXP.level >= 5 ? 'from-blue-500 to-cyan-500' :
                    'from-green-500 to-emerald-500'
                } text-white font-bold text-xs sm:text-sm shadow-lg`}>
                {userXP.level}
              </div>
            )}
          </div>
          <div className="flex-1 text-center sm:text-left w-full">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">{currentPlayer.username || currentPlayer.displayName}</h3>
            {userXP && (
              <p className="text-base sm:text-lg text-cyan-400 mb-3">{LEVEL_CONFIGS[userXP.level - 1]?.title || 'Kezdő'}</p>
            )}

            {/* XP Progress Bar */}
            {!isLoadingXP && userXP && (
              <div className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#B8B8C3]">
                    XP: {userXP.totalXP} / {userXP.level < LEVEL_CONFIGS.length ? LEVEL_CONFIGS[userXP.level].requiredXP : userXP.totalXP}
                  </span>
                  <span className="text-cyan-400 font-semibold">
                    {userXP.level < LEVEL_CONFIGS.length
                      ? Math.round((userXP.currentLevelXP / userXP.nextLevelXP) * 100)
                      : 100}%
                  </span>
                </div>
                <div className="w-full bg-[#15191E] rounded-full h-2 overflow-hidden border border-[#31383D]">
                  <div
                    className={`h-full bg-gradient-to-r ${userXP.level >= 15 ? 'from-purple-500 to-pink-500' :
                      userXP.level >= 10 ? 'from-yellow-500 to-orange-500' :
                        userXP.level >= 5 ? 'from-blue-500 to-cyan-500' :
                          'from-green-500 to-emerald-500'
                      } transition-all duration-500`}
                    style={{ width: `${userXP.level < LEVEL_CONFIGS.length ? Math.round((userXP.currentLevelXP / userXP.nextLevelXP) * 100) : 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2 sm:gap-4 flex-wrap justify-center sm:justify-start">
              <div className="flex items-center gap-2 bg-[#15191E] px-2 sm:px-3 py-2 rounded-lg">
                <span className="text-xl sm:text-2xl">🏆</span>
                <div>
                  <p className="text-xs text-[#B8B8C3]">{t('playerProfile.wins')}</p>
                  <p className="text-base sm:text-lg font-bold text-green-400">{stats.wins}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-[#15191E] px-2 sm:px-3 py-2 rounded-lg">
                <span className="text-xl sm:text-2xl">❌</span>
                <div>
                  <p className="text-xs text-[#B8B8C3]">{t('playerProfile.losses')}</p>
                  <p className="text-base sm:text-lg font-bold text-red-400">{stats.losses}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-[#15191E] px-2 sm:px-3 py-2 rounded-lg">
                <span className="text-xl sm:text-2xl">📊</span>
                <div>
                  <p className="text-xs text-[#B8B8C3]">{t('playerProfile.winRate')}</p>
                  <p className="text-base sm:text-lg font-bold text-cyan-400">{stats.winRate}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 sm:flex gap-2 border-b border-[#31383D]">
        <button
          onClick={() => setActiveTab('stats')}
          className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${activeTab === 'stats'
            ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
            : 'text-[#B8B8C3] hover:text-white'
            }`}
        >
          📊 {t('playerProfile.statistics')}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${activeTab === 'history'
            ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
            : 'text-[#B8B8C3] hover:text-white'
            }`}
        >
          📜 {t('playerProfile.matchHistory')}
        </button>
        <button
          onClick={() => setActiveTab('achievements')}
          className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${activeTab === 'achievements'
            ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
            : 'text-[#B8B8C3] hover:text-white'
            }`}
        >
          🏆 {t('playerProfile.achievements')}
        </button>
        {!viewingPlayer && (
          <button
            onClick={() => setActiveTab('friends')}
            className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${activeTab === 'friends'
              ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
              : 'text-[#B8B8C3] hover:text-white'
              }`}
          >
            👥 Barátok
          </button>
        )}
        {!viewingPlayer && (
          <button
            onClick={() => setActiveTab('operation')}
            className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${activeTab === 'operation'
              ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
              : 'text-[#B8B8C3] hover:text-white'
              }`}
          >
            🏢 Üzemeltetés
          </button>
        )}
        {!viewingPlayer && (
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-3 sm:px-6 py-3 font-semibold transition-colors text-sm sm:text-base whitespace-nowrap ${activeTab === 'settings'
              ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
              : 'text-[#B8B8C3] hover:text-white'
              }`}
          >
            ⚙️ {t('playerProfile.settings')}
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          {/* Stat Cards Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="card-dark p-4 sm:p-6 border-t-4 border-[#00AFF2]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#B8B8C3] text-sm sm:text-base">{t('playerProfile.totalGames')}</span>
                <span className="text-2xl sm:text-3xl">🎮</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white">{stats.totalGames}</p>
            </div>

            <div className="card-dark p-4 sm:p-6 border-t-4 border-[#00AFF2]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#B8B8C3] text-sm sm:text-base">{t('playerProfile.averageScore')}</span>
                <span className="text-2xl sm:text-3xl">📈</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#00AFF2]">{stats.averageScore}</p>
            </div>

            <div className="card-dark p-4 sm:p-6 border-t-4 border-[#d9a514]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#B8B8C3] text-sm sm:text-base">{t('playerProfile.highestCheckout')}</span>
                <span className="text-2xl sm:text-3xl">🎯</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#d9a514]">{stats.highestCheckout}</p>
            </div>

            <div className="card-dark p-4 sm:p-6 border-t-4 border-[#2bc48a]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#B8B8C3] text-sm sm:text-base">{t('playerProfile.total180s')}</span>
                <span className="text-2xl sm:text-3xl">💯</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#2bc48a]">{stats.total180s}</p>
            </div>

            <div className="card-dark p-4 sm:p-6 border-t-4 border-[#00AFF2]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#B8B8C3] text-sm sm:text-base">{t('playerProfile.checkoutRate')}</span>
                <span className="text-2xl sm:text-3xl">✅</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white">{stats.checkoutRate}%</p>
            </div>

            <div className="card-dark p-4 sm:p-6 border-t-4 border-[#00AFF2]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#B8B8C3] text-sm sm:text-base">{t('playerProfile.playTime')}</span>
                <span className="text-2xl sm:text-3xl">⏱️</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white">{formatPlayTime(stats.totalPlayTime)}</p>
            </div>
          </div>

          {/* Level Progression Section */}
          {!isLoadingXP && userXP && userXP.level && (
            <div className="card-dark p-6">
              <h3 className="text-2xl font-bold text-white mb-4">🎯 {t('profile.levelProgression')}</h3>
              <div className="space-y-3">
                {LEVEL_CONFIGS.slice(Math.max(0, userXP.level - 2), Math.min(LEVEL_CONFIGS.length, userXP.level + 3)).map((config) => (
                  <div
                    key={config.level}
                    className={`p-4 rounded-lg flex items-center justify-between ${config.level === userXP.level
                      ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-2 border-cyan-500'
                      : config.level < userXP.level
                        ? 'bg-green-500/10'
                        : 'bg-[#15191E]'
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${config.level === userXP.level
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                        : config.level < userXP.level
                          ? 'bg-green-500 text-white'
                          : 'bg-[#31383D] text-[#B8B8C3]'
                        }`}>
                        {config.level}
                      </div>
                      <div>
                        <p className="text-white font-semibold">{config.title}</p>
                        <p className="text-sm text-[#B8B8C3]">{config.requiredXP.toLocaleString()} XP</p>
                      </div>
                    </div>
                    {config.level === userXP.level && (
                      <span className="px-3 py-1 bg-cyan-500 text-white rounded-full text-sm font-semibold">
                        {t('profile.current')}
                      </span>
                    )}
                    {config.level < userXP.level && (
                      <span className="text-green-400 text-2xl">✓</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* XP History Section - Collapsible */}
          {!isLoadingXP && userXP && xpHistory.length > 0 && (
            <div className="card-dark">
              <button
                onClick={() => setIsXPHistoryExpanded(!isXPHistoryExpanded)}
                className="w-full p-6 flex items-center justify-between hover:bg-[#252d3f] transition-colors rounded-t-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">⚡</span>
                  <h3 className="text-2xl font-bold text-white">{t('profile.recentXPGains')}</h3>
                  <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-semibold">
                    {xpHistory.length}
                  </span>
                </div>
                <svg
                  className={`w-6 h-6 text-[#B8B8C3] transition-transform ${isXPHistoryExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isXPHistoryExpanded && (
                <div className="p-6 pt-0 space-y-2 border-t border-[#31383D]">
                  {xpHistory.map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg hover:bg-[#252d3f] transition-colors">
                      <div className="flex items-center gap-4">
                        <span className="text-3xl">
                          {transaction.source === 'match' ? '⚔️' :
                            transaction.source === 'tournament' ? '🏆' :
                              transaction.source === 'achievement' ? '🎖️' :
                                transaction.source === 'daily' ? '📅' : '🎁'}
                        </span>
                        <div>
                          <p className="text-white font-semibold">{transaction.description}</p>
                          <p className="text-sm text-[#B8B8C3]">
                            {transaction.timestamp.toLocaleDateString()} • {transaction.timestamp.toLocaleTimeString()}
                          </p>
                          {transaction.metadata?.matchResult && (
                            <span className={`text-xs px-2 py-1 rounded mt-1 inline-block ${transaction.metadata.matchResult === 'win'
                              ? 'bg-green-500/20 text-green-400'
                              : transaction.metadata.matchResult === 'loss'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                              {transaction.metadata.matchResult === 'win' ? t('profile.victory') :
                                transaction.metadata.matchResult === 'loss' ? t('profile.defeat') :
                                  t('profile.draw')}
                            </span>
                          )}
                          {transaction.metadata?.tournamentPosition && (
                            <span className="text-xs px-2 py-1 rounded mt-1 inline-block bg-purple-500/20 text-purple-400 ml-2">
                              {transaction.metadata.tournamentPosition}. {t('profile.place')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold text-xl">+{transaction.amount}</p>
                        <p className="text-xs text-[#B8B8C3]">XP</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card-dark overflow-x-auto">
          <table className="min-w-full divide-y divide-[#31383D]">
            <thead className="bg-[#1d232a]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('playerProfile.opponent')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('playerProfile.result')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('playerProfile.score')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('playerProfile.gameMode')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('playerProfile.venue')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('playerProfile.date')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#15191E] divide-y divide-[#31383D]">
              {matchHistory.map((match) => (
                <tr key={match.id} className="hover:bg-[#1d232a] transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-medium">
                    {match.opponent}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {match.result === 'win' ? (
                      <span className="px-3 py-1 rounded-full bg-[#2bc48a]/10 text-[#2bc48a] border border-[#2bc48a]/30 font-semibold">
                        {t('playerProfile.win')}
                      </span>
                    ) : match.result === 'draw' ? (
                      <span className="px-3 py-1 rounded-full bg-[#d9a514]/10 text-[#d9a514] border border-[#d9a514]/30 font-semibold">
                        Döntetlen
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full bg-[#ff6b6b]/10 text-[#ff6b6b] border border-[#ff6b6b]/30 font-semibold">
                        {t('playerProfile.loss')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">
                    {match.score}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#B8B8C3]">
                    {match.gameMode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#B8B8C3]">
                    {match.venue}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#B8B8C3]">
                    {new Date(match.date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'achievements' && (
        <div className="space-y-6">
          {/* Filter and Sort Controls */}
          <div className="card-dark p-4 space-y-4">
            {/* Rendezés Label */}
            <p className="text-sm text-[#B8B8C3]">Rendezés:</p>

            {/* Dropdowns Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Status Filter Dropdown */}
              <select
                value={achievementFilter}
                onChange={(e) => setAchievementFilter(e.target.value as any)}
                className="bg-[#1d232a] text-white px-4 py-2 rounded-lg border border-[#31383D] focus:border-[#00AFF2] focus:outline-none text-sm font-semibold"
              >
                <option value="all">🏆 Összes ({achievements.length})</option>
                <option value="unlocked">✅ Elért ({achievements.filter(a => a.unlocked).length})</option>
                <option value="locked">🔒 Zárolt ({achievements.filter(a => !a.unlocked).length})</option>
              </select>

              {/* Sort Dropdown */}
              <select
                value={achievementSort}
                onChange={(e) => setAchievementSort(e.target.value as any)}
                className="bg-[#1d232a] text-white px-4 py-2 rounded-lg border border-[#31383D] focus:border-[#00AFF2] focus:outline-none text-sm"
              >
                <option value="unlocked-first">Elért eredmények felül</option>
                <option value="locked-first">Elért eredmények alul</option>
                <option value="name">Név szerint</option>
                <option value="rarity">Ritkaság szerint</option>
              </select>
            </div>

            {/* Category Filter Buttons */}
            <div className="border-t border-[#31383D] pt-4">
              <p className="text-xs text-[#B8B8C3] mb-2 uppercase tracking-wider">Kategória:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setAchievementCategory('all')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${achievementCategory === 'all'
                    ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                    : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                    }`}
                >
                  Összes
                </button>
                <button
                  onClick={() => setAchievementCategory('wins')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${achievementCategory === 'wins'
                    ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                    : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                    }`}
                >
                  🏆 Győzelmek
                </button>
                <button
                  onClick={() => setAchievementCategory('matches')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${achievementCategory === 'matches'
                    ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                    : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                    }`}
                >
                  🎮 Mérkőzések
                </button>
                <button
                  onClick={() => setAchievementCategory('friends')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${achievementCategory === 'friends'
                    ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                    : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                    }`}
                >
                  👥 Barátok
                </button>
                <button
                  onClick={() => setAchievementCategory('skill')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${achievementCategory === 'skill'
                    ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                    : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                    }`}
                >
                  🎯 Ügyesség
                </button>
                <button
                  onClick={() => setAchievementCategory('other')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${achievementCategory === 'other'
                    ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                    : 'bg-[#1d232a] text-[#B8B8C3] hover:bg-[#31383D]'
                    }`}
                >
                  ⭐ Egyéb
                </button>
              </div>
            </div>
          </div>

          {/* Achievements Grid */}
          {isLoadingAchievements ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
            </div>
          ) : achievements.length === 0 ? (
            <div className="card-dark p-12 text-center">
              <div className="text-6xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-white mb-2">Nincsenek elérhető eredmények</h3>
              <p className="text-[#B8B8C3]">
                Jelenleg nincsenek elérhető eredmények a rendszerben.
              </p>
            </div>
          ) : (() => {
            // Filter achievements by status
            let filteredAchievements = achievements;
            if (achievementFilter === 'unlocked') {
              filteredAchievements = achievements.filter(a => a.unlocked);
            } else if (achievementFilter === 'locked') {
              filteredAchievements = achievements.filter(a => !a.unlocked);
            }

            // Filter by category
            if (achievementCategory !== 'all') {
              filteredAchievements = filteredAchievements.filter(a => {
                const category = a.category?.toLowerCase() || '';
                const reqType = a.requirementType?.toLowerCase() || '';

                switch (achievementCategory) {
                  case 'wins':
                    return category === 'wins' || reqType.includes('win') || reqType.includes('győzelem');
                  case 'matches':
                    return category === 'matches' || reqType.includes('match') || reqType.includes('mérkőzés') || reqType.includes('játék');
                  case 'friends':
                    return category === 'friends' || category === 'social' || reqType.includes('friend') || reqType.includes('barát');
                  case 'skill':
                    return category === 'skill' || reqType.includes('180') || reqType.includes('checkout') || reqType.includes('average');
                  case 'other':
                    return !['wins', 'matches', 'friends', 'skill'].includes(category);
                  default:
                    return true;
                }
              });
            }

            // Sort achievements
            const sortedAchievements = [...filteredAchievements].sort((a, b) => {
              switch (achievementSort) {
                case 'name':
                  return a.title.localeCompare(b.title);
                case 'unlocked-first':
                  if (a.unlocked === b.unlocked) return 0;
                  return a.unlocked ? -1 : 1;
                case 'locked-first':
                  if (a.unlocked === b.unlocked) return 0;
                  return a.unlocked ? 1 : -1;
                case 'rarity':
                  const rarityOrder = { legendary: 0, epic: 1, rare: 2, common: 3 };
                  return (rarityOrder[a.rarity as keyof typeof rarityOrder] || 3) - (rarityOrder[b.rarity as keyof typeof rarityOrder] || 3);
                default:
                  return 0;
              }
            });

            return filteredAchievements.length === 0 ? (
              <div className="card-dark p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-bold text-white mb-2">Nincs találat</h3>
                <p className="text-[#B8B8C3]">
                  Nincs eredmény a kiválasztott szűrővel.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedAchievements.map((achievement) => (
                  <div
                    key={achievement.id}
                    className={`card-dark p-6 transition-all relative ${achievement.unlocked
                      ? 'border-2 border-[#d9a514] shadow-[0_0_20px_rgba(217,165,20,0.3)]'
                      : 'opacity-50 grayscale'
                      }`}
                  >
                    {/* Rarity Badge */}
                    {achievement.rarity && (
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${achievement.rarity === 'legendary'
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-[0_0_10px_rgba(251,191,36,0.5)]'
                          : achievement.rarity === 'epic'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-[0_0_10px_rgba(168,85,247,0.5)]'
                            : achievement.rarity === 'rare'
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-[0_0_10px_rgba(59,130,246,0.5)]'
                              : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                          }`}>
                          {achievement.rarity === 'legendary' && '⭐ '}
                          {achievement.rarity === 'epic' && '💎 '}
                          {achievement.rarity === 'rare' && '🔷 '}
                          {achievement.rarity === 'common' && '⚪ '}
                          {achievement.rarity === 'legendary' ? 'Legendás' :
                            achievement.rarity === 'epic' ? 'Epikus' :
                              achievement.rarity === 'rare' ? 'Ritka' : 'Közönséges'}
                        </span>
                      </div>
                    )}

                    <div className="text-center">
                      <div className="text-6xl mb-4">{achievement.icon}</div>
                      <h3 className="text-xl font-bold text-white mb-2">{achievement.title}</h3>
                      <p className="text-sm text-[#B8B8C3] mb-4">{achievement.description}</p>

                      {achievement.unlocked ? (
                        <div className="text-xs text-[#2bc48a] font-semibold">
                          ✅ {t('playerProfile.unlocked')}: {new Date(achievement.unlockedAt!).toLocaleDateString('hu-HU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="text-xs text-[#8a8f99]">
                            🔒 {t('playerProfile.locked')}
                          </div>
                          {achievement.requirementType && achievement.requirementValue && (() => {
                            const currentProgress = calculateAchievementProgress(achievement.requirementType);
                            const requiredValue = achievement.requirementValue;
                            const progressPercentage = Math.min((currentProgress / requiredValue) * 100, 100);

                            return (
                              <div className="mt-3 p-3 bg-[#15191E] rounded-lg border border-[#31383D]">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs text-[#B8B8C3]">Követelmény:</p>
                                  <p className="text-xs font-semibold text-white">
                                    {currentProgress}/{requiredValue}
                                  </p>
                                </div>

                                {/* Progress Bar */}
                                <div className="relative w-full h-2 bg-[#1d232a] rounded-full overflow-hidden">
                                  <div
                                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] transition-all duration-500 rounded-full"
                                    style={{ width: `${progressPercentage}%` }}
                                  />
                                </div>

                                <p className="text-xs text-[#B8B8C3] mt-2">
                                  {achievement.requirementType}
                                </p>

                                {progressPercentage > 0 && (
                                  <p className="text-xs text-[#00AFF2] font-semibold mt-1">
                                    {progressPercentage.toFixed(0)}% teljesítve
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'requests' && !viewingPlayer && (
        <div className="space-y-6">
          <div className="card-dark p-6">
            <h3 className="text-2xl font-bold text-white mb-6">📨 Barátkérések</h3>

            {friendRequests.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <h3 className="text-xl font-bold text-white mb-2">Nincsenek barátkérések</h3>
                <p className="text-[#B8B8C3]">
                  Jelenleg nincsenek függőben lévő barátkéréseid.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {friendRequests.map((request) => (
                  <div key={request.id} className="p-4 bg-[#1d232a] rounded-lg hover:bg-[#31383D] transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00AFF2] to-[#0089be] flex items-center justify-center">
                          <span className="text-white font-bold text-xl">{request.fromUsername.charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-white font-semibold text-lg">{request.fromUsername}</p>
                          <p className="text-sm text-[#B8B8C3]">
                            Kérés érkezett: {request.createdAt.toLocaleDateString('hu-HU', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => acceptFriendRequest(request)}
                          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-semibold flex items-center gap-2"
                        >
                          <span className="text-xl">✓</span>
                          Elfogad
                        </button>
                        <button
                          onClick={() => rejectFriendRequest(request.id)}
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-semibold flex items-center gap-2"
                        >
                          <span className="text-xl">✗</span>
                          Elutasít
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'friends' && !viewingPlayer && (
        <div className="space-y-6">
          <div className="card-dark p-6">
            {/* Sub-tabs */}
            <div className="flex gap-2 mb-6 border-b border-[#31383D]">
              <button
                onClick={() => setFriendsSubTab('friends')}
                className={`px-6 py-3 font-semibold transition-colors ${friendsSubTab === 'friends'
                  ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
                  : 'text-[#B8B8C3] hover:text-white'
                  }`}
              >
                👥 Barátok ({friends.length})
              </button>
              <button
                onClick={() => setFriendsSubTab('incoming')}
                className={`px-6 py-3 font-semibold transition-colors relative ${friendsSubTab === 'incoming'
                  ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
                  : 'text-[#B8B8C3] hover:text-white'
                  }`}
              >
                📨 Bejövő kérelmek ({friendRequests.length})
                {friendRequests.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {friendRequests.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setFriendsSubTab('outgoing')}
                className={`px-6 py-3 font-semibold transition-colors relative ${friendsSubTab === 'outgoing'
                  ? 'text-[#00AFF2] border-b-2 border-[#00AFF2]'
                  : 'text-[#B8B8C3] hover:text-white'
                  }`}
              >
                📤 Kimenő kérések ({outgoingRequests.length})
                {outgoingRequests.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {outgoingRequests.length}
                  </span>
                )}
              </button>
            </div>

            {/* Friends List */}
            {friendsSubTab === 'friends' && (
              <>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                  <h3 className="text-2xl font-bold text-white">👥 Barátok</h3>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => setShowPlayerSearch(!showPlayerSearch)}
                      className="btn-secondary flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none justify-center whitespace-nowrap text-xs sm:text-base px-2 sm:px-4"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span className="hidden xs:inline">{t('playerProfile.searchPlayers')}</span>
                      <span className="xs:hidden">Keresés</span>
                    </button>
                    <button
                      onClick={() => setShowAddFriendDialog(true)}
                      className="btn-primary flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none justify-center whitespace-nowrap text-xs sm:text-base px-2 sm:px-4"
                    >
                      <span className="text-base sm:text-xl">➕</span>
                      <span className="hidden xs:inline">Barát hozzáadása</span>
                      <span className="xs:hidden">Hozzáadás</span>
                    </button>
                  </div>
                </div>

                {isLoadingFriends ? (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">⏳</div>
                    <p className="text-[#B8B8C3]">Betöltés...</p>
                  </div>
                ) : friends.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">👥</div>
                    <h3 className="text-xl font-bold text-white mb-2">Még nincsenek barátaid</h3>
                    <p className="text-[#B8B8C3] mb-4">
                      Adj hozzá barátokat username alapján és kövesd teljesítményüket!
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {friends.map((friendship) => {
                      const friendUsername = friendship.userId1 === user?.uid ? friendship.username2 : friendship.username1;
                      return (
                        <div key={friendship.id} className="p-4 bg-[#1d232a] rounded-lg hover:bg-[#31383D] transition-colors">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00AFF2] to-[#0089be] flex items-center justify-center">
                                <span className="text-white font-bold text-lg">{friendUsername.charAt(0).toUpperCase()}</span>
                              </div>
                              <div>
                                <p className="text-white font-semibold">{friendUsername}</p>
                                <p className="text-xs text-[#B8B8C3]">Barátok: {friendship.createdAt.toLocaleDateString()}</p>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFriend(friendship.id)}
                            className="w-full px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors text-sm"
                          >
                            🗑️ Törlés
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Incoming Friend Requests */}
            {friendsSubTab === 'incoming' && (
              <>
                <h3 className="text-2xl font-bold text-white mb-6">📨 Bejövő kérelmek</h3>

                {friendRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📭</div>
                    <h3 className="text-xl font-bold text-white mb-2">Nincsenek bejövő kérelmek</h3>
                    <p className="text-[#B8B8C3]">
                      Jelenleg nincsenek függőben lévő barátkéréseid.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {friendRequests.map((request) => (
                      <div key={request.id} className="p-4 bg-[#1d232a] rounded-lg hover:bg-[#31383D] transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00AFF2] to-[#0089be] flex items-center justify-center">
                              <span className="text-white font-bold text-xl">{request.fromUsername.charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="text-white font-semibold text-lg">{request.fromUsername}</p>
                              <p className="text-sm text-[#B8B8C3]">
                                Kérés érkezett: {request.createdAt.toLocaleDateString('hu-HU', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => acceptFriendRequest(request)}
                              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-semibold flex items-center gap-2"
                            >
                              <span className="text-xl">✓</span>
                              Elfogad
                            </button>
                            <button
                              onClick={() => rejectFriendRequest(request.id)}
                              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-semibold flex items-center gap-2"
                            >
                              <span className="text-xl">✗</span>
                              Elutasít
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Outgoing Friend Requests */}
            {friendsSubTab === 'outgoing' && (
              <>
                <h3 className="text-2xl font-bold text-white mb-6">📤 Kimenő kérések</h3>

                {outgoingRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📭</div>
                    <h3 className="text-xl font-bold text-white mb-2">Nincsenek kimenő kérések</h3>
                    <p className="text-[#B8B8C3]">
                      Jelenleg nincsenek függőben lévő kimenő barátkéréseid.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {outgoingRequests.map((request) => (
                      <div key={request.id} className="p-4 bg-[#1d232a] rounded-lg hover:bg-[#31383D] transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00AFF2] to-[#0089be] flex items-center justify-center">
                              <span className="text-white font-bold text-xl">{request.toUsername.charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="text-white font-semibold text-lg">{request.toUsername}</p>
                              <p className="text-sm text-[#B8B8C3]">
                                Kérés elküldve: {request.createdAt.toLocaleDateString('hu-HU', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => cancelFriendRequest(request.id)}
                              className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-semibold flex items-center gap-2"
                            >
                              <span className="text-xl">🚫</span>
                              Visszavonás
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Friend Dialog */}
      {showAddFriendDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddFriendDialog(false)}>
          <div className="card-dark p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">➕ Barát hozzáadása</h3>
              <button onClick={() => setShowAddFriendDialog(false)} className="text-[#B8B8C3] hover:text-white">
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  Felhasználónév
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={friendSearchTerm}
                    onChange={(e) => setFriendSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchUserByUsername()}
                    placeholder="pl. andrew_foldesi"
                    className="input-dark flex-1"
                  />
                  <button
                    onClick={searchUserByUsername}
                    disabled={isLoadingFriends}
                    className="btn-primary"
                  >
                    🔍 Keresés
                  </button>
                </div>
              </div>

              {friendMessage && (
                <div className={`p-3 rounded-lg ${friendMessage.includes('elküldve') || friendMessage.includes('találat')
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                  {friendMessage}
                </div>
              )}

              {searchedUser && (
                <div className="p-4 bg-[#1d232a] rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00AFF2] to-[#0089be] flex items-center justify-center">
                        <span className="text-white font-bold text-lg">
                          {searchedUser.username?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-semibold">{searchedUser.username}</p>
                        <p className="text-xs text-[#B8B8C3]">{searchedUser.displayName}</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={sendFriendRequest}
                    disabled={isLoadingFriends}
                    className="w-full btn-primary"
                  >
                    📨 Barátkérés küldése
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && !viewingPlayer && (
        <div className="space-y-6">
          {/* Profile Information */}
          <div className="card-dark p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">👤 {t('playerProfile.profileInfo')}</h3>
              {!editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('playerProfile.editProfile')}
                </button>
              )}
            </div>

            <div className="space-y-4">
              {isGoogleUser && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-400 flex items-start gap-3">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold">{t('playerProfile.googleAccountInfo')}</p>
                    <p className="text-sm mt-1">{t('playerProfile.googleAccountDesc')}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('playerProfile.displayName')}
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={!editMode}
                  className="input-dark w-full"
                  placeholder={t('playerProfile.displayNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  Felhasználónév
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  disabled={!editMode}
                  className="input-dark w-full"
                  placeholder="pl. janos123"
                />
              </div>

              {!isGoogleUser && (
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('playerProfile.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={!editMode}
                    className="input-dark w-full"
                    placeholder={t('playerProfile.emailPlaceholder')}
                  />
                </div>
              )}

              {editMode && !isGoogleUser && email !== user?.email && (
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('playerProfile.passwordForConfirmation')}
                  </label>
                  <input
                    type="password"
                    value={passwordForAuth}
                    onChange={(e) => setPasswordForAuth(e.target.value)}
                    className="input-dark w-full"
                    placeholder="••••••••"
                  />
                  <p className="text-xs text-[#B8B8C3] mt-1">{t('playerProfile.passwordForConfirmationHint')}</p>
                </div>
              )}

              {editMode && (
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleProfileUpdate}
                    className="btn-primary"
                  >
                    {t('playerProfile.saveChanges')}
                  </button>
                  <button
                    onClick={() => {
                      setDisplayName(user?.displayName || '');
                      setEmail(user?.email || '');
                      setPasswordForAuth('');
                      setEditMode(false);
                      setErrorMessage('');
                      loadUserUsername();
                    }}
                    className="btn-secondary"
                  >
                    {t('playerProfile.cancel')}
                  </button>
                </div>
              )}

              {saveMessage && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
                  ✅ {saveMessage}
                </div>
              )}

              {errorMessage && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                  ❌ {errorMessage}
                </div>
              )}
            </div>
          </div>

          {/* Change Password - Only for non-Google users */}
          {!isGoogleUser && (
            <div className="card-dark p-6">
              <h3 className="text-2xl font-bold text-white mb-6">🔒 {t('playerProfile.changePassword')}</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('playerProfile.currentPassword')}
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="input-dark w-full"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('playerProfile.newPassword')}
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-dark w-full"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('playerProfile.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-dark w-full"
                    placeholder="••••••••"
                  />
                </div>

                <button
                  onClick={handlePasswordUpdate}
                  className="btn-primary"
                >
                  {t('playerProfile.updatePassword')}
                </button>
              </div>
            </div>
          )}

          {/* Account Settings */}
          <div className="card-dark p-6">
            <h3 className="text-2xl font-bold text-white mb-6">⚙️ {t('playerProfile.accountSettings')}</h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#1d232a] rounded-lg">
                <div>
                  <p className="text-white font-semibold">{t('playerProfile.notifications')}</p>
                  <p className="text-sm text-[#B8B8C3]">{t('playerProfile.notificationsDesc')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00AFF2]"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#1d232a] rounded-lg">
                <div>
                  <p className="text-white font-semibold">{t('playerProfile.publicProfile')}</p>
                  <p className="text-sm text-[#B8B8C3]">{t('playerProfile.publicProfileDesc')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00AFF2]"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'operation' && !viewingPlayer && user && (
        <VenueRequestForm userId={user.uid} />
      )}
    </div>
  );
};

export default PlayerProfile;
